import { Member } from '@/features/iam/members/Member'
import {
  type BookkeepingPlatformConnection,
  type BookkeepingPlatformConnectionProps,
  createBookkeepingPlatformConnection,
  OtherAccount,
  QuickBooksAccount,
  XeroAccount,
} from '@/features/jobs/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { action, observable } from 'mobx'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'

export type BookkeepingAccessMethod = 'Managed' | 'Unmanaged' | 'Unspecified'

export type BookkeepingManagerAction = 'ConfirmAccess' | 'Unspecified'

export interface BookkeepingPendingActionProps extends ModelProps<BookkeepingPendingAction> {
  platformConnection?: BookkeepingPlatformConnectionProps
}

export class BookkeepingPendingAction extends Model<BookkeepingPendingAction> {
  @observable.ref
  private accessor _platformConnection: BookkeepingPlatformConnection | null =
    null

  id = Id.none

  @observable accessor jobShortId = ''
  @observable accessor customer: Member = Member.none
  @observable accessor championPro: Member = Member.none
  @observable accessor jobName = ''
  @observable accessor workspaceId = Id.none
  @observable accessor accessMethod: BookkeepingAccessMethod = 'Unspecified'
  @observable accessor action: BookkeepingManagerAction = 'Unspecified'
  @observable accessor dateCreated = unixEpoch()
  @observable accessor datePendingSince = unixEpoch()

  static get none() {
    return lazyNone()
  }

  get platformConnection() {
    return this._platformConnection
  }

  @action.bound
  override set({
    platformConnection,
    ...props
  }: Partial<BookkeepingPendingActionProps>): this {
    if (platformConnection) {
      this._setPlatformConnection(platformConnection)
    }

    return super.set(props)
  }

  // The concrete class depends on `type`, so the instance is only replaced
  // when the type changes, to preserve identity.
  private _setPlatformConnection(props: BookkeepingPlatformConnectionProps) {
    const current = this._platformConnection
    switch (props.type) {
      case 'QuickBooks':
        if (current instanceof QuickBooksAccount) {
          current.set(props.quickBooks)
          return
        }
        break
      case 'Xero':
        if (current instanceof XeroAccount) {
          current.set(props.xero)
          return
        }
        break
      case 'Other':
        if (current instanceof OtherAccount) {
          current.set(props.other)
          return
        }
        break
    }

    this._platformConnection = createBookkeepingPlatformConnection(props)
  }
}

const lazyNone = once(() => new BookkeepingPendingAction())
